import React, { FunctionComponent, useEffect, useState } from 'react';
import { Line, XAxis, YAxis, Tooltip, Legend,  Label, Scatter, ComposedChart } from 'recharts';
import moment from 'moment';
import '../../stations/Station.scss';
import { ONEDAY, getMaxFromSelectedFields, getMinFromSelectedFields } from 'src/store/common';

type WindHourlyChartProps = {
    all_dates: any[],
}

const CustomizedAxisTick: FunctionComponent<any> = (props: any) => {
  const { x, y, payload,timezoneOffset } = props;
  return (
    <g transform={`translate(${x},${y})`} >
      <text
        fontSize=".75rem"
        x={0}
        y={2}
        dy={16}
        textAnchor="end"
        fill="#666"
        transform="rotate(-35)"
      >
        {moment(payload.value).utc().utcOffset(timezoneOffset).format('MMM DD')}
      </text>
    </g>
  );
};
const CustomTooltip:any = (props:any) => {
  const {active, label,payload, timezoneOffset}=props;

  if (active && payload && payload.length) {
    return (
      <div id="custom-tooltip" className="custom-tooltip" style={{backgroundColor: 'white', border: 'none !important', display:'block', height:'5.25rem', width: '5.25rem'}}>
        <span className="month" style={{fontSize: '.75rem', paddingBottom: '0px', paddingTop:'0px', display:'block'}}>
        {moment(label).utc().utcOffset(timezoneOffset).format("MMM DD, YYYY")}
        </span>
        <span className="hours" style={{fontSize:'.75rem', paddingTop: '0px', paddingBottom: '0px', display:'block'}}>
        {moment(label).utc().utcOffset(timezoneOffset).format("HH:mm")}
        </span>
        <svg width="50" height="12" style={{display: 'block', marginTop: '5px'}}>
          <rect width="5" height="5" style={{fill:'#be0000'}}/>
          <text
            fontSize=".75rem"
            height="8"
            x={10}
            y={10}
          >
          {payload[0] && payload[0].value?`${payload[0].value}`:'null'}
        </text>
        </svg>
        <svg width="50" height="10" style={{display: 'block',marginTop: '5px'}}>
          <rect width="5" height="5" style={{fill:'#4C4E4E'}}/>
          <text
            fontSize=".75rem"
            height="8"
            x={10}
            y={10}
          >
          {payload[1] && payload[1].value?`${payload[1].value}`: 'null'}
        </text>
        </svg>
        <svg width="50" height="11" style={{display: 'block', marginTop: '5px'}}>
          <rect width="5" height="5" style={{fill:'blue'}}/>
          <text
            fontSize=".75rem"
            height="8"
            x={10}
            y={10}
          >
          {payload[2] && payload[2].value?`${payload[2].value}`:'null'}
        </text>
        </svg>
      </div>
    );
  }
  return null;
};

const RenderDot:FunctionComponent = (props:any) => {
  return (
    <circle cx={props.cx} cy={props.cy} r="2"fill={props.fill}/>
  )
}
export const WindHourlyChart: React.FC<WindHourlyChartProps> = ({
  all_dates
}) => {
  const [xTicks, setXTicks] = useState<number[]>([]);
  useEffect(()=>{
    if(all_dates.length>1){
      let ticks:number[]=[];

      if(ticks.length<1){
        let i = moment(all_dates[0].date).format('MMM DD');
        ticks.push(all_dates[0].date)
        let j=0;
        while(i !== moment(all_dates.at(-1).date).format('MMM DD')){          
          j=j+1;
          ticks.push(all_dates[0].date - j*ONEDAY)
          i = moment(all_dates[0].date - j*ONEDAY).utc().utcOffset(all_dates[0].offset).format('MMM DD')
        }
        setXTicks(ticks)
      }
  
    }
  },[all_dates]);
  return(
    <>
    {all_dates.length>1 &&
        <ComposedChart
          width={450}
          height={190}
          data={all_dates}
          margin={{
            top: (10),
            right: 5,
            left: 5,
            bottom: 10,
          }}
        >
          <XAxis dataKey="date" tick={<CustomizedAxisTick timezoneOffset={all_dates[0].offset}/>} interval={0} type="number" domain={[all_dates.at(-1).date, all_dates[0].date]} ticks={xTicks}/>          
          <YAxis yAxisId="left" 
            domain={[getMinFromSelectedFields(all_dates, ['obsGS', 'fcGS', 'fcWS', 'obsWS'], 5), getMaxFromSelectedFields(all_dates, ['obsGS', 'fcGS','fcWS', 'obsWS'])]}
            style={{fontSize: '0.7rem',fontFamily: 'serif'}} 
            axisLine={false}>
          <Label
            value="Speed"
            position="insideLeft"
            angle={-90}
            fontSize={'0.7rem'}
            style={{ textAnchor: 'middle'}}
          />
          </YAxis>
          <YAxis yAxisId="right" orientation="right" 
            domain={[getMinFromSelectedFields(all_dates, ['windDir'],50), getMaxFromSelectedFields(all_dates, ['windDir'])]}
            style={{fontSize: '0.7rem',fontFamily: 'serif'}} 
            axisLine={false}>
          <Label
            value="Direction"
            position="insideRight"
            angle={-90}
            fontSize={'0.7rem'}
            style={{ textAnchor: 'middle' }}
          />
          </YAxis>
          <Tooltip content={<CustomTooltip timezoneOffset={all_dates[0].offset} />} position={{ y: -5 }} />
          <Legend wrapperStyle={{fontSize:'11px', top:'160px'}} iconType="plainline" iconSize={20}/>
          <Scatter name="Wind Direction" dataKey="windDir" fill="#be0000" yAxisId ="right" shape={<RenderDot/>}/>
          <Line name="Wind Speed" type="monotone" yAxisId ="left" dataKey="obsWS" stroke="#4C4E4E" dot={ false} strokeWidth={2}/>
          <Line name="Wind Speed Forecast" type="monotone" yAxisId = "left" dataKey="fcWS" stroke="#4C4E4E" dot={false} strokeDasharray="5 5" strokeWidth={2}/>
          <Line name="Gust Speed" type="monotone" yAxisId ="left" dataKey="obsGS" stroke="blue" dot={ false} strokeWidth={2}/>
          <Line name="Gust Speed Forecast" type="monotone" yAxisId = "left" dataKey="fcGS" stroke="blue" dot={false} strokeDasharray="5 5" strokeWidth={2}/>
        </ComposedChart>
    
    }
    </>

  );
}